/**
 * chat_widget/src/widget/ui/VehicleSheet/index.tsx
 */

import type { FC, Dispatch, SetStateAction } from "react";
import type { VehicleLike } from "../../types/VehicleTypes";
import { getPrice } from "../../utils/formatter";
import { WhatsappIcon } from "../../../assets/WhatsappIcon";

function formatNumber(value: any, lang: string) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat(lang || "fr-FR", {
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(Math.round(n));
  }
}

export const VehicleSheet: FC<{
  v: VehicleLike;
  actions: string[];
  lang: string;
  onBookTestDrive: (carId: string) => void;
  onMoreInfo: (carId: string) => void;
  setSelectedCar: Dispatch<SetStateAction<any>>;
}> = ({ v, lang, setSelectedCar, onBookTestDrive, onMoreInfo, actions }) => {
  const photos: string[] = Array.isArray(v?.photos)
    ? v.photos.map((p) => (p && p.src ? String(p.src) : "")).filter(Boolean)
    : [];

  const title = [v?.marque, v?.modele].filter(Boolean).join(" ");

  const energie = String(v?.energie ?? "");
  const boite = String(v?.typeboite ?? "");
  const price = formatNumber(v?.prixttcaffiche, lang);
  const km = formatNumber(v?.kilometrage, lang);
  const year = v?.anneemodele ? String(v.anneemodele) : "";
  // Build options list (options or equipements)
  const rawOptions = v?.options?.option?.length
    ? v.options.option
    : typeof v?.options?.option === "object"
      ? [v.options.option]
      : v?.equipements?.equipement?.length
        ? v.equipements.equipement
        : typeof v?.equipements?.equipement === "object"
          ? [v.equipements.equipement]
          : [];

  const sortedOptions = Array.isArray(rawOptions)
    ? [...rawOptions].sort(function (a: any, b: any) {
        if (a?._cdata > b?._cdata) return 1;
        if (b?._cdata > a?._cdata) return -1;
        return 0;
      })
    : [];

  const carId = v?._id ? String(v._id) : "";
  console.log(v.user);
  const whatsapp = v?.user?.phonemobile?.length ? v.user.phonemobile : null;

  const handleActionClick = (actionLabel: string) => {
    if (!carId) return;
    // Defensive: in prod, a missing/invalid callback should not break click handling
    if (actionLabel === "RDV pour essai") {
      if (typeof onBookTestDrive === "function") onBookTestDrive(carId);
      return;
    }
    if (typeof onMoreInfo === "function") onMoreInfo(carId);
  };

  return (
    <div className="vcw-selected-vehicle">
      {/* Top image slider (horizontal scroll) */}

      {photos.length > 0 ? (
        <div
          className="vcw-selected-slider"
          role="region"
          aria-label="Photos du véhicule"
        >
          <div className="vcw-selected-slider-track">
            {photos.map((src, idx) => (
              <div
                className="vcw-selected-slider-slide"
                key={`${String(v?._id ?? "car")}_slide_${idx}`}
              >
                <img className="vcw-selected-slider-img" src={src} alt="" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="vcw-selected-slider vcw-selected-slider--empty"
          aria-hidden="true"
        />
      )}
      <button
        className="vcw-selected-close"
        onClick={() => setSelectedCar(null)}
        aria-label="Fermer"
      >
        ✕
      </button>

      {/* Details section (fills the remaining height) */}
      <div className="vcw-selected-details">
        <div className="vcw-selected-title">{title}</div>
        <div className="vcw-selected-version">{v?.version}</div>

        <div className="vcw-selected-specs">
          <div className="vcw-selected-specs-row">
            {year && <div className="vcw-selected-spec">{year}</div>}
            {km && <div className="vcw-selected-spec">{km} km</div>}
            {energie && <div className="vcw-selected-spec">{energie}</div>}
            {boite && <div className="vcw-selected-spec">{boite}</div>}
          </div>
        </div>
        {price && (
          <div className="vcw-selected-price">
            {price}
            <span style={{ fontSize: "15px" }}> € TTC</span>
          </div>
        )}

        {sortedOptions.length > 0 && (
          <div className="vcw-selected-options">
            {sortedOptions.map((opt: any, idx: number) => {
              const label = opt?._cdata ? String(opt._cdata) : "";
              if (!label) return null;
              return (
                <div
                  key={`option_${idx}`}
                  className="vcw-selected-option-badge"
                >
                  {label}
                </div>
              );
            })}
          </div>
        )}
        <div className="vcw-selected-cta" role="region" aria-label="Actions">
          {actions.map((label, idx) => (
            <button
              key={`${label}_${idx}`}
              type="button"
              className={`vcw-selected-cta-btn ${label !== "RDV pour essai" ? "outlined" : ""}`}
              data-vcw-action={label}
              data-vcw-carid={carId}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleActionClick(label);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {whatsapp?.length ? (
          <div className="whatsapp-container">
            <button
              className="vcw-selected-cta-btn whatsapp"
              onClick={() => {
                setTimeout(() => {
                  window.open(
                    `https://wa.me/33${whatsapp}/?text= Bonjour,%0A%20Je%20suis%20intéressé·e%20par%20votre%20${`${v.marque} ${v.modele}`}${
                      v.prixttcaffiche
                        ? `%20à%20${getPrice(
                            v.prixttcaffiche,
                          )}%20€%20que%20j'ai%20trouvé%20sur%20weespots.com`
                        : "."
                    }%20J’aimerais%20avoir%20plus%20d’informations%20sur%20ce%20véhicule.`,
                  );
                }, 200);
              }}
            >
              <WhatsappIcon className="" />
              WhatsApp
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
